import React, { useState, useEffect, useCallback, useMemo } from "react";
import TopNav from "../../commonUI/TopHeader/TopHeader";
import {
  getJobsRecordsCount,
  getretrieveJobs,
} from "../../../services/jobService";
import { showSpinner, closeSpinner, modal, alert } from "../../../js/modules/Dialogs";
import { sessionExpired } from "../../../config/messages";
import { toScreen } from "../../../js/modules/Routing";
import PropTypes from "prop-types";
import withNavigate from "../withNavigate";
import moment from "moment-timezone";
import Pagination from "../../commonUI/Pagination/Pagination";
import SearchFilterBar from "../../commonUI/SearchFilterBar/SearchFilterBar";
import DataTable from "../../commonUI/DataTable/DataTable";
import TabNav from "../../commonUI/TabNav/TabNav";
import {
  CloseCircleIcon,
  CheckCircleIcon,
  ClockIcon,
  HourglassIcon,
} from "../../commonUI/Icons/SvgIcon";

// --- Shared cell renderers ---
const renderClientCell = (job) => {
  const hasRefundAlert =
    (job.refundRequested &&
      (!job.refundAuthorisation || job.refundAuthorisation === "pending")) ||
    (job.status === "complete" &&
      job.paymentType === "fullpayment" &&
      (!job.mavPayment || job.mavPayment === ""));

  if (hasRefundAlert) {
    return (
      <span className="u-before-icon-exclamation u-color-red">
        &nbsp;&nbsp;{job?.firstname}
      </span>
    );
  }
  if (job.isPaused) {
    return (
      <span className="u-before-icon-pause text-capitalize">
        &nbsp;&nbsp;{job?.firstname || "-"}
      </span>
    );
  }
  return <span className="text-capitalize">{job?.firstname || "-"}</span>;
};

const renderLocationCell = (job) => (
  <div className="text-capitalize">
    <div>{job?.originAddress || ""}</div>
    {(job?.originAddress || job?.destinationAddress) && (
      <div className="text-sm text-gray-600">
        {job?.destinationAddress ? "  →" : ""}
        {job?.destinationAddress || ""}
      </div>
    )}
  </div>
);

const renderDateCell = (date) => (
  <>
    {moment(date)?.tz("Europe/London")?.format("DD MMM YYYY")}
    <br />
    <span className="text-sm text-gray-600">
      {moment(date)?.tz("Europe/London")?.format("hh:mm ")}
    </span>
  </>
);

const renderQuotesCell = (job) => (
  <>
    {job?.totalQuotes ?? 0}
    <br />
    <span className="text-sm text-gray-600">{"£280 – £340"}</span>
  </>
);

const renderStatusBadge = (job, tab) => {
  const isBooked = tab === "booked";

  const statusMap = {
    complete: {
      className: "badge-success",
      icon: <CheckCircleIcon />,
      label: "Completed",
    },
    closed: {
      className: "badge-danger",
      icon: <CloseCircleIcon />,
      label: isBooked ? "Cancelled by Driver" : "Cancelled by Client",
    },
    paid: {
      className: isBooked ? "badge-info" : "badge-warning",
      icon: isBooked ? <ClockIcon /> : <HourglassIcon />,
      label: isBooked ? "In Progress" : "Expired",
    },
  };

  const config = statusMap[job?.status] || { className: "", icon: null, label: job?.status };

  return (
    <span className={`badge ${config.className}`}>
      {config.icon}
      {config.label}
    </span>
  );
};

// --- Type & Status options ---
const TYPE_OPTIONS = [
  { value: "allJobTypes", label: "All Job Types (7)" },
  { value: "houseMove", label: "House Move (2)" },
  { value: "sendItems", label: "Send Items (5)" },
  { value: "wasteDisposal", label: "Waste Disposal (2)" },
];

const STATUS_OPTIONS = [
  { value: "allStatus", label: "All Status (7)" },
  { value: "inProgress", label: "In Progress (2)" },
  { value: "rebooked", label: "Rebooked (5)" },
  { value: "cancelled", label: "Cancelled (4)" },
  { value: "completed", label: "Completed (7)" },
  { value: "refunded", label: "Refunded (6)" },
];

// --- Main component ---
const Jobs = ({ userDetails, navigate }) => {
  const [searchValue, setSearchValue] = useState(null);
  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState(15);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [inputValue, setInputValue] = useState(1);
  const [activeTab, setActiveTab] = useState("open");
  const [filter, setFilter] = useState(
    window.history.state?.usr?.selectedFilter ?? "all"
  );
  const [typeFilter, setTypeFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [bookedStartDate, setBookedStartDate] = useState(null);
  const [bookedEndDate, setBookedEndDate] = useState(null);
  const [sort, setSort] = useState("name=-1");
  const [jobs, setJobs] = useState([]);
  const [activeFilters, setActiveFilters] = useState({
    open: null,
    booked: null,
    closed: null,
  });
  const [filterCount, setFilterCount] = useState({
    totalDocs: 0,
    openJobs: 0,
    closedJobs: 0,
    paidJobs: 0,
    completedJobs: 0,
    refundRequestedJobs: 0,
    transferNeededJobs: 0,
  });

  useEffect(() => {
    setInputValue(page);
  }, [page]);

  useEffect(() => {
    getJobs();
  }, [filter, page, pageSize, sort, search, activeFilters[activeTab]]);

  useEffect(() => {
    fetchFilterCount();
  }, []);

  const getJobs = async () => {
    showSpinner();
    const queryParams = {
      search,
      filter,
      sort,
      page,
      limit: pageSize,
      ...(activeFilters[activeTab] && { customFilter: activeFilters[activeTab] }),
    };
    try {
      const resJson = await getretrieveJobs(queryParams);
      closeSpinner();
      setTotalPages(Math.ceil(resJson?.totalRecords / pageSize));
      setJobs(resJson?.data || []);
    } catch (error) {
      closeSpinner();
      if (error.response?.status === 401) {
        modal(sessionExpired);
        toScreen(false, navigate, "/logout");
      } else {
        setJobs([]);
        alert(error.message || "Failed to fetch jobs");
      }
    }
  };

  const fetchFilterCount = async () => {
    showSpinner();
    try {
      const resJson = await getJobsRecordsCount();
      closeSpinner();
      setFilterCount({
        totalDocs: resJson?.totalDocs,
        openJobs: resJson?.openJobs,
        closedJobs: resJson?.closedJobs,
        paidJobs: resJson?.paidJobs,
        completedJobs: resJson?.completedJobs,
        refundRequestedJobs: resJson?.refundRequestedJobs,
        transferNeededJobs: resJson?.transferNeededJobs,
      });
    } catch (error) {
      closeSpinner();
      if (error.response?.status === 401) {
        modal(sessionExpired);
        toScreen(false, navigate, "/logout");
      } else {
        alert(error.message || "Failed to fetch filter count");
      }
    }
  };

  const searchJob = useCallback(() => {
    setSearch(searchValue);
    setFilter(searchValue === "" ? "open" : null);
    setPage(1);
  }, [searchValue]);

  const sortJobs = useCallback(
    (event, field) => {
      if (event) event.preventDefault();
      const sortFields = {
        client: { asc: "client=1", desc: "client=-1" },
        jobType: { asc: "jobType=1", desc: "jobType=-1" },
        location: { asc: "location=1", desc: "location=-1" },
        posted: { asc: "posted=1", desc: "posted=-1" },
        status: { asc: "status=1", desc: "status=-1" },
        booked: { asc: "booked=1", desc: "booked=-1" },
        quotes: { asc: "quotes=1", desc: "quotes=-1" },
        bookedFor: { asc: "bookedFor=1", desc: "bookedFor=-1" },
        name: { asc: "name=1", desc: "name=-1" },
        jobDate: { asc: "jobDate=1", desc: "jobDate=-1" },
        creationDate: { asc: "creationDate=1", desc: "creationDate=-1" },
      };
      const currentSort = sortFields[field];
      if (!currentSort) return;
      setSearch("");
      setSort(sort === currentSort.asc ? currentSort.desc : currentSort.asc);
      setPage(1);
    },
    [sort]
  );

  const getSortClass = (key) => {
    if (sort === `${key}=1`) return "sorting-icon sorting-icon-up";
    if (sort === `${key}=-1`) return "sorting-icon sorting-icon-down";
    return "";
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setPage(1);
    setFilter(tab);
  };

  const handleReset = () => {
    setActiveTab("open");
    setSort("name=-1");
    setSearchValue(null);
    setSearch("");
    setTypeFilter(null);
    setStatusFilter(null);
    setStartDate(null);
    setEndDate(null);
    setBookedStartDate(null);
    setBookedEndDate(null);
    setActiveFilters({ open: null, booked: null, closed: null });
    setFilter("open");
    setPage(1);
  };

  const SortButton = useCallback(
    ({ field, label }) => (
      <button onClick={(e) => sortJobs(e, field)} className="sort-button">
        {label}
        {sort.includes(`${field}=`) && <span className={getSortClass(field)} />}
      </button>
    ),
    [sort]
  );

  // --- Row renderers ---
  const renderViewBtn = (job) => (
    <button
      type="button"
      className="custom-btn"
      onClick={(e) => toScreen(e, navigate, "/job/" + job?.shortId)}
    >
      View
    </button>
  );

  const renderOpenRow = useCallback(
    (job, index) => (
      <tr key={job?._id || index}>
        <td>{renderClientCell(job)}</td>
        <td>{"House Move"}</td>
        <td>{renderLocationCell(job)}</td>
        <td>{renderDateCell(job?.jobCreationDate)}</td>
        <td>{renderQuotesCell(job)}</td>
        <td>{renderViewBtn(job)}</td>
      </tr>
    ),
    [navigate]
  );

  const renderBookedRow = useCallback(
    (job, index) => (
      <tr key={job?._id || index}>
        <td>{renderClientCell(job)}</td>
        <td>{"£230.00"}</td>
        <td>{renderStatusBadge(job, "booked")}</td>
        <td>{renderLocationCell(job)}</td>
        <td>{renderDateCell(job?.jobCreationDate)}</td>
        <td>{renderDateCell(job?.jobCreationDate)}</td>
        <td>{renderQuotesCell(job)}</td>
        <td>{renderViewBtn(job)}</td>
      </tr>
    ),
    [navigate]
  );

  const renderClosedRow = useCallback(
    (job, index) => (
      <tr key={job?._id || index}>
        <td>{renderClientCell(job)}</td>
        <td>{"Waste Disposal"}</td>
        <td>{renderStatusBadge(job, "closed")}</td>
        <td>{renderLocationCell(job)}</td>
        <td>{renderDateCell(job?.jobCreationDate)}</td>
        <td>{renderQuotesCell(job)}</td>
        <td>{renderViewBtn(job)}</td>
      </tr>
    ),
    [navigate]
  );

  // --- Tab config: headers + row renderers ---
  const tabConfig = useMemo(
    () => ({
      open: {
        colSpan: 6,
        renderRow: renderOpenRow,
        renderHeader: () => (
          <>
            <th><SortButton field="name" label="Client" /></th>
            <th><SortButton field="jobDate" label="Job Type" /></th>
            <th><SortButton field="location" label="Location" /></th>
            <th><SortButton field="creationDate" label="Posted" /></th>
            <th><SortButton field="quotes" label="Quotes" /></th>
            <th></th>
          </>
        ),
      },
      booked: {
        colSpan: 8,
        renderRow: renderBookedRow,
        renderHeader: () => (
          <>
            <th><SortButton field="name" label="Client" /></th>
            <th><SortButton field="jobDate" label="Booked For" /></th>
            <th><SortButton field="status" label="Status" /></th>
            <th><SortButton field="location" label="Location" /></th>
            <th><SortButton field="posted" label="Posted" /></th>
            <th><SortButton field="booked" label="Booked" /></th>
            <th><SortButton field="quotes" label="Quotes" /></th>
            <th></th>
          </>
        ),
      },
      closed: {
        colSpan: 7,
        renderRow: renderClosedRow,
        renderHeader: () => (
          <>
            <th><SortButton field="name" label="Client" /></th>
            <th><SortButton field="jobType" label="Job Type" /></th>
            <th><SortButton field="status" label="Status" /></th>
            <th><SortButton field="location" label="Location" /></th>
            <th><SortButton field="posted" label="Posted" /></th>
            <th><SortButton field="quotes" label="Quotes" /></th>
            <th></th>
          </>
        ),
      },
    }),
    [SortButton, renderOpenRow, renderBookedRow, renderClosedRow]
  );

  // --- SearchFilterBar filters (dynamic per activeTab) ---
  const filters = useMemo(() => {
    const list = [
      {
        type: "select",
        key: "type",
        label: "Type",
        value: typeFilter,
        onChange: setTypeFilter,
        options: TYPE_OPTIONS,
        placeholder: "All Job Types",
      },
    ];

    if (activeTab !== "open") {
      list.push({
        type: "select",
        key: "status",
        label: "Status",
        value: statusFilter,
        onChange: setStatusFilter,
        options: STATUS_OPTIONS,
        placeholder: "All Status",
      });
    }

    list.push({
      type: "dateRange",
      key: "posted",
      label: "Posted",
      value: { start: startDate, end: endDate },
      onChange: ({ start, end }) => {
        setStartDate(start);
        setEndDate(end);
      },
      placeholder: "Anytime",
    });

    if (activeTab === "booked") {
      list.push({
        type: "dateRange",
        key: "booked",
        label: "Booked",
        value: { start: bookedStartDate, end: bookedEndDate },
        onChange: ({ start, end }) => {
          setBookedStartDate(start);
          setBookedEndDate(end);
        },
        placeholder: "Anytime",
      });
    }

    return list;
  }, [activeTab, typeFilter, statusFilter, startDate, endDate, bookedStartDate, bookedEndDate]);

  return (
    <>
      <TopNav title="Jobs" />

      <div className="global-body">
        <div className="global-body-container">
          <TabNav
            activeTab={activeTab}
            onChange={handleTabChange}
            tabs={[
              { key: "open", label: "Open", count: filterCount?.openJobs || 0 },
              { key: "booked", label: "Booked", count: filterCount?.completedJobs || 0 },
              { key: "closed", label: "Closed", count: filterCount?.closedJobs || 0 },
            ]}
          />

          <SearchFilterBar
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            onSearchSubmit={searchJob}
            searchPlaceholder="Search..."
            filters={filters}
            onReset={handleReset}
          />

          <DataTable
            data={jobs}
            columns={[]}
            renderRow={tabConfig[activeTab]?.renderRow}
            onSort={sortJobs}
            getSortClass={getSortClass}
            renderHeader={tabConfig[activeTab]?.renderHeader}
            emptyMessage="No Jobs found"
            tableClassName="table clientTable"
            colSpan={tabConfig[activeTab]?.colSpan}
          />

          <Pagination
            pageSize={pageSize}
            totalPages={totalPages}
            page={page}
            setPage={setPage}
            inputValue={inputValue}
            setInputValue={setInputValue}
            setPageSize={setPageSize}
          />
        </div>
      </div>
    </>
  );
};

Jobs.propTypes = {
  userDetails: PropTypes.object.isRequired,
  updateState: PropTypes.func.isRequired,
  navigate: PropTypes.func.isRequired,
};

export default withNavigate(Jobs);
